import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext, useEffect, useState } from 'react'
import { FolderDriveItem } from '../components/Document/DocumentFlatTree'

// Session cache of a single file's folder-only tree, keyed by documentRootId. The tree is loaded by
// the DocumentCombobox the first time the "Standaarddocument maken" dialog is opened (a batched
// Graph crawl); its result is persisted here so subsequent opens — for any party in the file —
// reuse it instead of re-crawling. Cleared when the file changes or a folder is created/renamed/deleted.
export type FileFolderTreeContextType = {
    folders: FolderDriveItem[] | undefined
    // Persist the folder tree the combobox just loaded, so later opens reuse it.
    setFolders: (items: FolderDriveItem[]) => void
    // Drop the cached tree. Call after a folder create/rename/move/delete so the next open reloads.
    invalidate: () => void
}

const FileFolderTreeContext = createContext<FileFolderTreeContextType>({
    folders: undefined,
    setFolders: () => {},
    invalidate: () => {},
})

export function FileFolderTreeProvider({ documentRootId, children }: PropsWithChildren<{ documentRootId?: string }>): ReactElement {
    const [folders, setFolders] = useState<FolderDriveItem[]>()

    // Drop the cached tree when the file (documentRootId) changes so we never show one file's folders
    // for another. (The combobox only persists on dialog-open, long after this mount effect, so there
    // is no race with a just-loaded result.)
    useEffect(() => {
        setFolders(undefined)
    }, [documentRootId])

    const invalidate = () => setFolders(undefined)

    return <FileFolderTreeContext.Provider value={{ folders, setFolders, invalidate }}>{children}</FileFolderTreeContext.Provider>
}

export function useFileFolderTree(): FileFolderTreeContextType {
    return useContext(FileFolderTreeContext)
}

export default FileFolderTreeContext
